import { useEffect } from "react";

const SITE_URL = "https://aiglchannel.info";
const LOGO_URL = `${SITE_URL}/icons/icon-512.png`;
const SCRIPT_KEY = "product-itemlist";
const ORG_SCRIPT_KEY = "homepage-organization";

/**
 * Homepage Organization schema — carries the aggregateRating signal so
 * Google can render star reviews in branded SERP results. The 3 testimonials
 * (Marcus, Amara, Sophie) are testimonials of the AIGL brand, so the rating
 * sits on Organization (not on individual Products) to remain policy-safe.
 *
 * If/when we accumulate ≥10 verified customer reviews via the /review flow,
 * we can swap this static block for a dynamic one fed by the API.
 */
const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "AIGL Channel",
  alternateName: "AIGL Career Launch",
  url: SITE_URL,
  logo: LOGO_URL,
  description:
    "Investor-ready business plans for high-demand industries. Cyber Security, Security Guard, VIP Protection, Home Care, Recruitment, and more. Instant download.",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "3",
    bestRating: "5",
    worstRating: "1",
  },
};

function buildProductSchema(p) {
  // Anchor on the landing page's product card; deep-link CTA scrolls into view.
  const targetUrl = `${SITE_URL}/#products`;
  return {
    "@type": "Product",
    name: p.name,
    description: p.short_description || p.tagline || p.name,
    sku: p.id,
    brand: { "@type": "Brand", name: "AIGL Channel" },
    image: LOGO_URL,
    url: targetUrl,
    offers: {
      "@type": "Offer",
      url: targetUrl,
      priceCurrency: "USD",
      price: Number(p.price_usd ?? p.price ?? 0).toFixed(2),
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "AIGL Channel" },
    },
  };
}

/**
 * Injects an ItemList of Product schemas into <head>.
 * Google can surface these as a rich-result carousel on branded/category searches.
 * Idempotent: removes prior copy on each render, cleans up on unmount.
 */
export default function ProductSchema({ products }) {
  useEffect(() => {
    if (!Array.isArray(products) || products.length === 0) return;

    const data = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "AIGL Business Plans",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: products.length,
      itemListElement: products.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: buildProductSchema(p),
      })),
    };

    document.head
      .querySelectorAll(`script[data-aigl-jsonld="${SCRIPT_KEY}"]`)
      .forEach((s) => s.remove());

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.aiglJsonld = SCRIPT_KEY;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);

    // Organization schema with aggregateRating — enables star ratings in
    // branded Google Search results.
    document.head
      .querySelectorAll(`script[data-aigl-jsonld="${ORG_SCRIPT_KEY}"]`)
      .forEach((s) => s.remove());
    const orgScript = document.createElement("script");
    orgScript.type = "application/ld+json";
    orgScript.dataset.aiglJsonld = ORG_SCRIPT_KEY;
    orgScript.text = JSON.stringify(ORGANIZATION_SCHEMA);
    document.head.appendChild(orgScript);

    return () => {
      script.remove();
      orgScript.remove();
    };
  }, [products]);

  return null;
}
